export const METHODS = {
  get: 'GET',
  post: 'POST',
  put: 'PUT',
  patch: 'PATCH',
  delete: 'DELETE',
  options: 'OPTIONS',
} as const

export type Method = (typeof METHODS)[keyof typeof METHODS]

export type HttpLikeError = Error & {
  status?: number
  response?: Response
  data?: unknown
  code?: string
  isHttpError?: boolean
  isTimeout?: boolean
  isAbort?: boolean
  isNetworkError?: boolean
  originalError?: unknown
}

export type ErrorCodeMap = { [key: number]: (error: HttpLikeError) => string }

export const defaultErrorCodeMap: ErrorCodeMap = {
  400: () => 'Bad Request: The server cannot process the request due to a client error.',
  401: () => 'Unauthorized: Authentication is required to access this resource.',
  403: () => 'Forbidden: The server understood the request, but refuses to authorize it.',
  404: () => 'Not Found: The requested resource could not be found.',
}

interface RequestTarget {
  url: string
  method: Method
}

export interface RequestBody {
  body: unknown
  params: unknown
}

type RequestInitWithoutBody = Omit<RequestInit, 'body' | 'params' | 'method'>

export interface Config extends RequestInitWithoutBody, Partial<RequestTarget>, Partial<RequestBody>, Partial<Interceptor> {
  baseURL?: string
  headers?: Record<string, string>
  options?: Record<string, unknown>
  errors?: Record<string, unknown> & { verbose?: boolean }
  timeout?: number
  responseType?: 'json' | 'blob' | 'arraybuffer' | 'text' | 'formData'
}

export type FetchParams = string | string[][] | Record<string, string> | URLSearchParams

type InterceptorEntry<T> = {
  id: number
  handler: T
}

type InterceptorBuckets = {
  request: InterceptorEntry<RequestInterceptor>[]
  response: InterceptorEntry<ResponseInterceptor>[]
  error: InterceptorEntry<ErrorInterceptor>[]
  timeout: InterceptorEntry<TimeoutInterceptor>[]
  race: InterceptorEntry<RaceInterceptor>[]
}

type Interceptor = Record<'interceptors', InterceptorBuckets>

type RaceEvent = { type: 'start'; timestamp: number } | { type: 'end'; timestamp: number; duration: number }

interface RaceInterceptor {
  (event: RaceEvent, value: Config): void
}

interface RequestInterceptor {
  (requestConfig: Partial<Config>): Partial<Config> | Promise<Partial<Config>>
}

interface ResponseInterceptor {
  (response: Response): Response | Promise<Response>
}

interface ErrorInterceptor {
  (error: unknown): unknown | Promise<unknown>
}

interface TimeoutInterceptor {
  (timeout: number): void
}

interface InterceptorId {
  id: number
  type: 'request' | 'response' | 'error' | 'timeout' | 'race'
}

interface Instance {
  instanciate: (options: Partial<Config>) => {
      create: <T>(method: Method, uri: string, ...requestConfig: Config[]) => Promise<T>
      helpers: {
          isHttpLikeError: (error: unknown) => error is HttpLikeError
          responseError: (error: unknown, errorCodeMap?: ErrorCodeMap) => Promise<never>
          errorCodeMap: ErrorCodeMap
      }
      interceptors: {
          timeout: {
              (interceptor: TimeoutInterceptor, errorInterceptor?: ErrorInterceptor): InterceptorId
              eject: (interceptorId: InterceptorId) => boolean
          }
          request: {
              (interceptor: RequestInterceptor, errorInterceptor?: ErrorInterceptor): InterceptorId
              eject: (interceptorId: InterceptorId) => boolean
          }
          response: {
              (interceptor: ResponseInterceptor, errorInterceptor?: ErrorInterceptor): InterceptorId
              eject: (interceptorId: InterceptorId) => boolean
          }
          race: {
              (interceptor: RaceInterceptor, errorInterceptor?: ErrorInterceptor): InterceptorId
              eject: (interceptorId: InterceptorId) => boolean
          }
      }
      defaults: Config
  }
}

const defaultHeaders: Record<string, string> = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
}

const createEmptyInterceptors = (): InterceptorBuckets => ({
  request: [],
  response: [],
  error: [],
  timeout: [],
  race: [],
})

const init: Config = {
  baseURL: '',
  headers: defaultHeaders,
  interceptors: createEmptyInterceptors(),
  errors: { verbose: false },
}

let interceptorIdCounter = 0

function race(interceptor: RaceInterceptor, buckets: InterceptorBuckets, error?: ErrorInterceptor): InterceptorId {
  const id = interceptorIdCounter++
  buckets.race.push({ id, handler: interceptor })
  if (error) {
      buckets.error.push({ id, handler: error })
  }
  return { id, type: 'race' }
}

function timeout(interceptor: TimeoutInterceptor, buckets: InterceptorBuckets, error?: ErrorInterceptor): InterceptorId {
  const id = interceptorIdCounter++
  buckets.timeout.push({ id, handler: interceptor })
  if (error) {
      buckets.error.push({ id, handler: error })
  }
  return { id, type: 'timeout' }
}

function request(interceptor: RequestInterceptor, buckets: InterceptorBuckets, error?: ErrorInterceptor): InterceptorId {
  const id = interceptorIdCounter++
  buckets.request.push({ id, handler: interceptor })
  if (error) {
      buckets.error.push({ id, handler: error })
  }
  return { id, type: 'request' }
}

function response(interceptor: ResponseInterceptor, buckets: InterceptorBuckets, error?: ErrorInterceptor): InterceptorId {
  const id = interceptorIdCounter++
  buckets.response.push({ id, handler: interceptor })
  if (error) {
      buckets.error.push({ id, handler: error })
  }
  return { id, type: 'response' }
}

function removeInterceptorById<T>(list: InterceptorEntry<T>[], id: number): boolean {
  const index = list.findIndex((entry) => entry.id === id)
  if (index !== -1) {
      list.splice(index, 1)
      return true
  }
  return false
}

const ejectMap: Record<InterceptorId['type'], (id: number, buckets: InterceptorBuckets) => boolean> = {
  request: (id, buckets) => removeInterceptorById(buckets.request, id) || removeInterceptorById(buckets.error, id),
  response: (id, buckets) => removeInterceptorById(buckets.response, id) || removeInterceptorById(buckets.error, id),
  timeout: (id, buckets) => removeInterceptorById(buckets.timeout, id) || removeInterceptorById(buckets.error, id),
  error: (id, buckets) => removeInterceptorById(buckets.error, id),
  race: (id, buckets) => removeInterceptorById(buckets.race, id) || removeInterceptorById(buckets.error, id),
}

function eject(interceptorId: InterceptorId, buckets: InterceptorBuckets): boolean {
  const { id, type } = interceptorId
  return ejectMap[type](id, buckets)
}

type InterceptorsFunctionsMap = {
  request: (value: Config) => Promise<Config>
  response: (response: Response) => Promise<Response>
  error: (error: unknown) => Promise<unknown>
  timeout: (timeout: number) => number
  race: (event: RaceEvent, value: Config) => void
}

function createInterceptorsMap(interceptors: InterceptorBuckets): InterceptorsFunctionsMap {
  return {
      request: async (value: Config): Promise<Config> => {
          let config = { ...value }
          for (const { handler } of interceptors.request) {
              const result = await handler(config)
              if (result) {
                  config = { ...config, ...result }
              }
          }
          return config
      },
      response: async (response: Response): Promise<Response> => {
          let res = response
          for (const { handler } of interceptors.response) {
              const result = await handler(res)
              if (result) {
                  res = result
              }
          }
          return res
      },
      error: async (error: unknown): Promise<unknown> => {
          let err = error
          for (const { handler } of interceptors.error) {
              const result = await handler(err)
              if (result !== undefined) {
                  err = result
              }
          }
          return err
      },
      timeout: (timeout: number): number => {
          for (const { handler } of interceptors.timeout) {
              handler(timeout)
          }
          return timeout
      },
      race: (event: RaceEvent, value: Config): void => {
          for (const { handler } of interceptors.race) {
              handler(event, value)
          }
      },
  }
}

function timeoutPromise(ms: number, controller: AbortController, notifyTimeout: InterceptorsFunctionsMap['timeout']): { promise: Promise<never>; clear: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined

  const promise = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
          if (!controller.signal.aborted) {
              controller.abort()
              void notifyTimeout(ms)
              reject(new Error('Request timed out'))
          }
      }, ms)

      controller.signal.addEventListener('abort', () => clearTimeout(timer))
  })

  // The timer must be cleared once the request settles, otherwise it still fires after a successful response
  return { promise, clear: () => clearTimeout(timer) }
}

async function buildResponseError(response: Response): Promise<Error> {
  let message = `${response.status} ${response.statusText || 'Request failed'}`
  let parsedData: unknown

  try {
      parsedData = await response.clone().json()
      const readableMessage = extractErrorMessage(parsedData)
      if (readableMessage) {
          message = `${readableMessage}`
      }
  } catch {
      try {
          const text = await response.clone().text()
          if (text) {
              message += `: ${text.trim()}`
          }
      } catch {
          // ignore parsing errors
      }
  }

  const error = new Error(message) as HttpLikeError
  error.name = 'HttpError'
  error.status = response.status
  error.response = response
  error.code = `E_HTTP_${response.status}`
  error.isHttpError = true

  if (parsedData !== undefined) {
      error.data = parsedData
  }

  return error
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function firstStringFromArray(value: unknown): string | undefined {
  if (!Array.isArray(value)) {
      return undefined
  }

  for (const item of value) {
      if (typeof item === 'string' && item.trim().length > 0) {
          return item.trim()
      }
  }

  return undefined
}

function extractErrorMessage(data: unknown): string | undefined {
  if (typeof data === 'string') {
      return data.trim() || undefined
  }

  if (!isRecord(data)) {
      return undefined
  }

  const directKeys = ['message', 'error', 'detail', 'title', 'description']
  for (const key of directKeys) {
      const value = data[key]
      if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim()
      }
  }

  if (isRecord(data.errors)) {
      for (const value of Object.values(data.errors)) {
          if (typeof value === 'string' && value.trim().length > 0) {
              return value.trim()
          }

          const firstArrayString = firstStringFromArray(value)
          if (firstArrayString) {
              return firstArrayString
          }
      }
  }

  return undefined
}

function normalizeRequestError(error: unknown): HttpLikeError {
  if (error instanceof Error && ('status' in error || 'response' in error || error.name === 'HttpError')) {
      return error as HttpLikeError
  }

  const message = error instanceof Error && error.message ? error.message : 'Request failed'

  const normalized = new Error(message) as HttpLikeError
  normalized.originalError = error

  if (error instanceof Error && error.stack) {
      normalized.stack = error.stack
  }

  if (message === 'Request timed out') {
      normalized.name = 'TimeoutError'
      normalized.code = 'E_TIMEOUT'
      normalized.isTimeout = true
      return normalized
  }

  if (error instanceof Error && error.name === 'AbortError') {
      normalized.name = 'AbortError'
      normalized.code = 'E_ABORT'
      normalized.isAbort = true
      if (!normalized.message) {
          normalized.message = 'Request aborted'
      }
      return normalized
  }

  if (error instanceof TypeError) {
      normalized.name = 'NetworkError'
      normalized.code = 'E_NETWORK'
      normalized.isNetworkError = true
      if (!normalized.message) {
          normalized.message = 'Network request failed'
      }
  }

  return normalized
}

export function isHttpLikeError(error: unknown): error is HttpLikeError {
  return typeof error === 'object' && error !== null && ('status' in error || 'message' in error)
}

function responseError(error: unknown, errorCodeMap: ErrorCodeMap = defaultErrorCodeMap): Promise<never> {
  if (isHttpLikeError(error) && typeof error.status === 'number') {
      const mappedError = errorCodeMap[error.status]
      if (mappedError) {
          console.error(mappedError(error), error)
          return Promise.reject(error)
      }
  }

  if (error instanceof Error) {
      console.error(error.message, error)
  } else {
      console.error('Request failed', error)
  }

  return Promise.reject(error)
}

async function doRequest<T>(requestConfig: Config): Promise<T> {
  const config = { ...requestConfig }
  const { url, timeout = 30_000, responseType = 'json', interceptors = createEmptyInterceptors() } = config

  if (!url) {
      throw new Error('Request URL is required')
  }

  const runners = createInterceptorsMap(interceptors)

  const controller = new AbortController()
  const signal = controller.signal
  config.signal = requestConfig.signal ?? signal

  const startTime = performance.now()
  runners.race({ type: 'start', timestamp: startTime }, config)

  const requestTimeout = timeoutPromise(timeout, controller, runners.timeout)

  try {
      const modifiedConfig = await runners.request(config)

      const response = await Promise.race<Response>([fetch(modifiedConfig.url ?? url, modifiedConfig as RequestInit), requestTimeout.promise])

      if (!response.ok) {
          throw await buildResponseError(response)
      }

      const interceptedResponse = await runners['response'](response)

      if (interceptedResponse.status === 204) {
          return {} as T
      }

      return await processResponse<T>(interceptedResponse, responseType)
  } catch (error) {
      const normalizedError = normalizeRequestError(error)
      const processedError = await runners.error(normalizedError)
      return Promise.reject(processedError)
  } finally {
      requestTimeout.clear()
      const endTime = performance.now()
      runners.race({ type: 'end', timestamp: endTime, duration: endTime - startTime }, config)
  }
}

type ResponseProcessor = (response: Response) => Promise<unknown>

const responseTypeMap: Record<string, ResponseProcessor> = {
  blob: (response) => response.blob(),
  arraybuffer: (response) => response.arrayBuffer(),
  text: (response) => response.text(),
  formData: (response) => response.formData(),
  json: (response) => response.json(),
}

async function processResponse<T>(response: Response, responseType: string): Promise<T> {
  const processor = responseTypeMap[responseType] || responseTypeMap['json']
  return (await processor?.(response)) as T
}

type ParamProcessor<T> = (params: T) => string

type ParamProcessorMap = {
  string: ParamProcessor<string>
  object: ParamProcessor<Record<string, unknown>>
  array: ParamProcessor<string[][]>
  urlsearchparams: ParamProcessor<URLSearchParams>
}

const paramProcessorMap: ParamProcessorMap = {
  string: (params: string) => params,
  object: (params: Record<string, unknown>) => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
              searchParams.append(key, String(value))
          }
      })
      return searchParams.toString()
  },
  array: (params: string[][]) => new URLSearchParams(params).toString(),
  urlsearchparams: (params: URLSearchParams) => params.toString(),
}

function processParams(params: unknown): string {
  if (!params) return ''

  if (typeof params === 'string') {
      return paramProcessorMap['string']?.(params) ?? ''
  } else if (params instanceof URLSearchParams) {
      return paramProcessorMap['urlsearchparams']?.(params) ?? ''
  } else if (Array.isArray(params)) {
      return paramProcessorMap['array']?.(params) ?? ''
  } else if (typeof params === 'object' && params !== null) {
      return paramProcessorMap.object(params as Record<string, unknown>)
  }

  return ''
}

function serializeBody(body: unknown): string | FormData | URLSearchParams | Blob | ArrayBuffer | null | undefined {
  if (body === undefined || body === null) {
      return body
  }

  if (body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob || body instanceof ArrayBuffer) {
      return body
  }

  if (typeof body === 'string') {
      return body
  }

  if (typeof body === 'object') {
      return JSON.stringify(body)
  }

  return String(body)
}

function createRequestConfig(method: Method, uri: string, values: Config[]): Config {
  const combinedValues: Config = Object.assign({}, ...values)
  const base = combinedValues.baseURL || init.baseURL || (typeof window !== 'undefined' ? window.location.origin : undefined)
  const url = base ? new URL(uri, base) : new URL(uri)

  if (combinedValues?.params) {
      url.search = processParams(combinedValues.params)
  }

  const { body, headers, ...rest } = combinedValues

  const serializedBody = serializeBody(body)
  const mergedHeaders: Record<string, string> = {
      ...defaultHeaders,
      ...init.headers,
      ...headers,
  }

  const hasCustomContentType = Object.keys(headers ?? {}).some((key) => key.toLowerCase() === 'content-type')

  if (serializedBody instanceof FormData || serializedBody instanceof Blob || serializedBody instanceof ArrayBuffer || serializedBody instanceof URLSearchParams) {
      if (!hasCustomContentType) {
          delete mergedHeaders['Content-Type']
      }
  }

  const newConfig: Config = {
      ...init,
      ...rest,
      method,
      headers: mergedHeaders,
      body: serializedBody,
      url: url.href,
  }

  return newConfig
}

export const fetchInstance: Instance = {
  instanciate: (values: Config = {}) => {
      const instanceInterceptors = createEmptyInterceptors()

      const instanceDefaults: Config = {
          ...init,
          ...values,
          interceptors: instanceInterceptors,
          headers: { ...init.headers, ...values.headers },
      }

      return {
          create: async <T>(method: Method, uri: string, ...values: Config[]): Promise<T> => {
              const config = createRequestConfig(method, uri, [{ ...instanceDefaults }, ...values])
              return await doRequest<T>(config)
          },
          helpers: {
              isHttpLikeError,
              responseError,
              errorCodeMap: { ...defaultErrorCodeMap },
          },
          interceptors: {
              request: Object.assign((interceptor: RequestInterceptor, error?: ErrorInterceptor) => request(interceptor, instanceInterceptors, error), {
                  eject: (interceptorId: InterceptorId) => eject(interceptorId, instanceInterceptors),
              }),
              response: Object.assign((interceptor: ResponseInterceptor, error?: ErrorInterceptor) => response(interceptor, instanceInterceptors, error), {
                  eject: (interceptorId: InterceptorId) => eject(interceptorId, instanceInterceptors),
              }),
              timeout: Object.assign((interceptor: TimeoutInterceptor, error?: ErrorInterceptor) => timeout(interceptor, instanceInterceptors, error), {
                  eject: (interceptorId: InterceptorId) => eject(interceptorId, instanceInterceptors),
              }),
              race: Object.assign((interceptor: RaceInterceptor, error?: ErrorInterceptor) => race(interceptor, instanceInterceptors, error), {
                  eject: (interceptorId: InterceptorId) => eject(interceptorId, instanceInterceptors),
              }),
          },
          defaults: { ...instanceDefaults, headers: { ...instanceDefaults.headers } },
      }
  },
}
